import { useEffect, useRef } from 'react';

type Handler = (e: KeyboardEvent) => void;
type Map = Record<string, Handler>;

interface Options {
  enabled?: boolean;
  ignoreWhenTyping?: boolean;
  sequenceTimeoutMs?: number;
}

function isTyping(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || (el as HTMLElement).isContentEditable === true;
}

export function useHotkeys(map: Map, opts: Options = {}): void {
  const { enabled = true, ignoreWhenTyping = true, sequenceTimeoutMs = 600 } = opts;
  const sequenceRef = useRef<{ keys: string[]; resetAt: number } | null>(null);
  const mapRef = useRef(map);
  mapRef.current = map;

  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      const key = e.key;
      const typing = isTyping(document.activeElement);
      const alwaysFireKeys = new Set(['Escape']);
      if (typing && ignoreWhenTyping && !alwaysFireKeys.has(key)) return;

      const direct = mapRef.current[key];

      const now = Date.now();
      let seq = sequenceRef.current;
      if (seq && now > seq.resetAt) seq = null;

      const candidateSequence = seq ? [...seq.keys, key] : [key];
      const sequenceKey = candidateSequence.join(' ');
      const seqHandler = mapRef.current[sequenceKey];

      const startsWith = candidateSequence.join(' ') + ' ';
      const hasFutureMatch = Object.keys(mapRef.current).some((k) => k.startsWith(startsWith));

      if (seqHandler) {
        sequenceRef.current = null;
        seqHandler(e);
        return;
      }

      if (hasFutureMatch) {
        sequenceRef.current = { keys: candidateSequence, resetAt: now + sequenceTimeoutMs };
        return;
      }

      sequenceRef.current = null;
      if (direct) direct(e);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [enabled, ignoreWhenTyping, sequenceTimeoutMs]);
}
